import React, { useState } from 'react';
import { Layers, ImageOff, PlayCircle, PauseCircle, XCircle, CheckCircle, Bookmark } from 'lucide-react';

const SerieCard = ({ franchise, onClick }) => {
  const [imgError, setImgError] = useState(false);

  const displayTitle = franchise.english || franchise.romaji || franchise.french || "Franchise inconnue";

  // Extraire l'anime "actif"
  // Ordre de priorité: watching > plan_to_watch > on_hold > dropped > completed
  const statusPriority = {
    'watching': 1,
    'plan_to_watch': 2,
    'on_hold': 3,
    'dropped': 4,
    'completed': 5
  };

  const sortedAnimes = [...(franchise.animes || [])].sort((a, b) => {
    const rankA = statusPriority[a.mal_status] || 99;
    const rankB = statusPriority[b.mal_status] || 99;
    if (rankA !== rankB) return rankA - rankB;

    // Si même statut, prioriser le plus récent (en utilisant end_watch_date ou ID)
    const timeA = new Date(a.end_watch_date || 0).getTime();
    const timeB = new Date(b.end_watch_date || 0).getTime();
    if (timeA !== timeB) return timeB - timeA;

    return (b.id || 0) - (a.id || 0);
  });

  const activeAnime = sortedAnimes.length > 0 ? sortedAnimes[0] : null;
  const seasonsCount = franchise.animes 
    ? new Set(franchise.animes.map(a => a.season_id || a.id)).size 
    : 0;

  const extractSeasonTitle = (title) => {
    if (!title) return null;

    const finalPattern = /(?:Final Season|The Final Season|Saison Finale)/i;
    if (finalPattern.test(title)) {
      return "Saison Finale";
    }

    const seasonPattern = /(?:(?:Season|Saison)\s*(\d+)|(\d+)(?:st|nd|rd|th)\s*(?:Season|Saison))/i;
    const match = title.match(seasonPattern);
    if (match) {
      const num = match[1] || match[2];
      return `Saison ${num}`;
    }

    const partPattern = /(?:Part\s*(\d+)|Cour\s*(\d+))/i;
    const pMatch = title.match(partPattern);
    if (pMatch) {
      const num = pMatch[1] || pMatch[2];
      return `Partie ${num}`;
    }

    return null;
  };

  let activeTitle = "";
  if (activeAnime) {
    const title = activeAnime.english || activeAnime.romaji || activeAnime.french || activeAnime.voiranime_slug;
    const seasonTitle = extractSeasonTitle(title);

    if (seasonsCount <= 1) {
      activeTitle = title;
    } else {
      activeTitle = seasonTitle ? seasonTitle : title;
    }
  }

  const getStatusConfig = (status) => {
    switch (status) {
      case 'watching': return { icon: PlayCircle, color: 'text-blue-400', bg: 'bg-blue-500/10', label: 'En cours' };
      case 'completed': return { icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-500/10', label: 'Terminé' };
      case 'on_hold': return { icon: PauseCircle, color: 'text-orange-400', bg: 'bg-orange-500/10', label: 'En pause' };
      case 'dropped': return { icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10', label: 'Abandonné' };
      case 'plan_to_watch': return { icon: Bookmark, color: 'text-purple-400', bg: 'bg-purple-500/10', label: 'À voir' };
      default: return { icon: PlayCircle, color: 'text-gray-400', bg: 'bg-gray-500/10', label: 'Inconnu' };
    }
  };

  const statusConfig = activeAnime ? getStatusConfig(activeAnime.mal_status) : null;
  const StatusIcon = statusConfig?.icon;

  return (
    <div
      className="relative aspect-[2/3] rounded-xl overflow-hidden shadow-lg group bg-gray-900 cursor-pointer border-2 border-transparent hover:border-indigo-500/50 transition-all duration-300"
      onClick={onClick}
    >
      <div className="absolute inset-0 z-0">
        {franchise.cover_image_url && !imgError ? (
          <img
            src={franchise.cover_image_url}
            alt={displayTitle}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 opacity-90 group-hover:opacity-100"
            onError={() => setImgError(true)}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-600 bg-gray-800">
            <ImageOff size={48} />
          </div>
        )}
      </div>

      <div className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/20 z-10 pointer-events-none"></div>



      {/* --- ZONE CONTENU (Overlay en bas) --- */}
      <div className="absolute bottom-0 left-0 right-0 z-20 p-4 bg-gradient-to-t from-gray-900/90 via-gray-900/60 to-transparent pt-24 flex flex-col justify-end">

        {activeAnime && statusConfig && activeAnime.mal_status !== 'completed' && (
          <div className="mb-2 inline-block">
            <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg backdrop-blur-md border border-white/5 ${statusConfig.bg}`}>
              <StatusIcon size={16} className={statusConfig.color} />
              <span className="text-xs font-medium text-gray-200 line-clamp-1">
                <span className="text-white font-semibold">{activeTitle}</span> • {statusConfig.label}
              </span>
            </div>
          </div>
        )}

        <h3 className="text-lg font-bold leading-tight line-clamp-2 text-white group-hover:text-indigo-300 transition-colors text-balance" title={displayTitle}>
          {displayTitle}
        </h3>
      </div>
    </div>
  );
};

export default SerieCard;
